import { useCallback, useEffect, useState } from 'react';
import { Check, Star, X } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface ReviewRow {
  id: string;
  sku: string;
  customer_name: string;
  skin_type: string | null;
  rating: number;
  review_text: string;
  verified_purchase: boolean;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

const STATUS_TONE: Record<string, 'success' | 'warning' | 'error'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'error',
};

export default function AdminProductReviews() {
  const { push } = useToast();
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('rxk_product_reviews')
      .select('id, sku, customer_name, skin_type, rating, review_text, verified_purchase, status, created_at')
      .order('created_at', { ascending: false });
    setReviews((data as ReviewRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id: string, status: 'approved' | 'rejected') => {
    setBusyId(id);
    const { error } = await supabase.from('rxk_product_reviews').update({ status }).eq('id', id);
    setBusyId(null);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', status === 'approved' ? 'Review approved' : 'Review rejected');
    await load();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('rxk_product_reviews').delete().eq('id', id);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Review deleted');
    await load();
  };

  const pendingCount = reviews.filter((r) => r.status === 'pending').length;
  const filtered = filter === 'all' ? reviews : reviews.filter((r) => r.status === filter);

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <h1 className="text-[22px] font-bold text-ink">Product Reviews</h1>

        <Card>
          <CardHeader
            title="Customer Reviews"
            subtitle={`${pendingCount} pending · ${reviews.length} total`}
            icon={<Star className="w-4 h-4" />}
            action={
              <div className="flex items-center gap-1.5">
                {(['pending', 'approved', 'rejected', 'all'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`text-[11px] px-2.5 py-1 rounded-full transition-colors cursor-pointer capitalize ${
                      filter === f ? 'bg-ink text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            }
          />
          <CardBody className="space-y-2">
            {loading ? (
              <Spinner />
            ) : filtered.length === 0 ? (
              <EmptyState title="No reviews here" />
            ) : (
              filtered.map((r) => (
                <div key={r.id} className="p-3 rounded-lg border border-border-subtle">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-semibold text-ink truncate">
                        {r.customer_name}
                        {r.skin_type && <span className="text-ink-faint font-normal"> · {r.skin_type}</span>}
                      </p>
                      <p className="text-[11px] text-ink-faint">{r.sku} · {r.created_at.slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {r.verified_purchase && <Badge tone="info">Verified</Badge>}
                      <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mt-2">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'fill-warning text-warning' : 'text-border-subtle'}`} />
                    ))}
                  </div>
                  <p className="text-[13px] text-ink mt-2">{r.review_text}</p>

                  <div className="flex gap-2 mt-3">
                    {r.status !== 'approved' && (
                      <Button size="sm" onClick={() => setStatus(r.id, 'approved')} disabled={busyId === r.id}>
                        <Check className="w-3.5 h-3.5" /> Approve
                      </Button>
                    )}
                    {r.status !== 'rejected' && (
                      <Button size="sm" variant="secondary" onClick={() => setStatus(r.id, 'rejected')} disabled={busyId === r.id}>
                        <X className="w-3.5 h-3.5" /> Reject
                      </Button>
                    )}
                    <InlineConfirm label="Delete" confirmLabel="Confirm delete" onConfirm={() => handleDelete(r.id)} />
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
